import { createClient, type SupabaseClient } from "@supabase/supabase-js";

/**
 * Supabase client for berserkr-app.
 *
 * Connects to the berserkr-app Supabase project (separate from Lawdogg).
 * Cross-project reads of Lawdogg data happen through `postgres_fdw` on
 * the database side — from the SPA's perspective, everything is a query
 * against berserkr-app.
 *
 * Auth posture:
 *   - The anon key is safe to expose in the client. RLS does the real
 *     protection.
 *   - For v0, the SPA queries against the anon role and relies on Supabase
 *     Auth (Email OTP or custom JWT minted from Cloudflare Access identity)
 *     to populate request.jwt.claims->>'email' for RLS predicates.
 *   - Until the Supabase Auth flow is wired (a future step), queries will
 *     succeed but RLS will reject privileged rows — the UI handles the
 *     resulting empty state gracefully.
 *
 * Configuration state:
 *   - When VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY are placeholders,
 *     getSupabase() returns null. Callers should handle this as
 *     "not configured" (component renders a hint, not a crash).
 */

let cached: SupabaseClient | null = null;

function isPlaceholder(value: string | undefined): boolean {
  if (!value) return true;
  return (
    value.includes("YOUR_PROJECT_REF") ||
    value.includes("YOUR_ANON_KEY") ||
    value.includes("YOUR_TEAM") ||
    value.length < 20
  );
}

export function getSupabase(): SupabaseClient | null {
  if (cached) return cached;

  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (isPlaceholder(url) || isPlaceholder(anonKey)) {
    return null;
  }

  cached = createClient(url, anonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
    db: {
      // The berserkr-app schema lives under the `berserkr` Postgres schema,
      // not `public`. The Supabase JS client respects this via .schema()
      // chain on individual queries; we keep the default here for any
      // public-schema queries (e.g., Supabase-managed tables).
      schema: "public",
    },
  });
  return cached;
}

export function isSupabaseConfigured(): boolean {
  return getSupabase() !== null;
}

/**
 * Helper for querying the berserkr schema. Returns null if Supabase
 * isn't configured.
 *
 *   const { data, error } = await berserkr().from('cases').select('*');
 */
export function berserkr() {
  const client = getSupabase();
  if (!client) {
    throw new Error(
      "Supabase not configured — call isSupabaseConfigured() first.",
    );
  }
  return client.schema("berserkr");
}
